import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChartLine, User, MagnifyingGlass, List, X, CaretDown, UserCircle, Book, Envelope, Globe, Briefcase, ShieldCheck } from '@phosphor-icons/react';
import useI18n from '../hooks/useI18n';
import FlagIcon from './FlagIcon';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const navItemsBase = [
  { path: '/', tKey: 'nav.about', icon: User, hasDropdown: true },
  { path: '/ciclo-economico', tKey: 'nav.cycle', icon: ChartLine },
  { path: '/analisi', tKey: 'nav.stocks', icon: MagnifyingGlass },
];

const chiSonoSections = [
  { id: 'about-bio-section', label: 'Su di Me', labelEn: 'About Me', icon: UserCircle },
  { id: 'about-projects-section', label: 'I miei Progetti', labelEn: 'My Projects', icon: Book },
  { id: 'about-works-section', label: 'Lavori', labelEn: 'Work', icon: Briefcase },
  { id: 'about-contact-section', label: 'Contatti', labelEn: 'Contact', icon: Envelope },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang, t } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const timeoutRef = useRef(null);
  const navItems = navItemsBase.map((n) => ({ ...n, label: t(n.tKey) }));

  // Admin session badge: checked on mount (and on login/logout elsewhere via
  // the 'admin-auth-changed' event, since AdminPanel lives on a different page).
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) { setIsAdmin(false); return; }
      try {
        const res = await fetch(`${API_BASE}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
        setIsAdmin(res.ok);
      } catch (e) { setIsAdmin(false); }
    };
    checkAdmin();
    window.addEventListener('admin-auth-changed', checkAdmin);
    return () => window.removeEventListener('admin-auth-changed', checkAdmin);
  }, []);

  useEffect(() => {
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, []);

  const handleDropdownEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const scrollToSection = (sectionId) => {
    setDropdownOpen(false);
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 left-0 right-0 z-50 py-3 px-4"
      role="navigation"
      aria-label="Navigazione principale"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo - Left */}
        <Link
          to="/"
          onClick={() => { if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          className="flex items-center gap-2.5 group flex-shrink-0"
          data-testid="navbar-logo"
        >
          <img
            src="/favicon.svg"
            alt="Gabriele Denaro"
            width="36"
            height="36"
            className="w-9 h-9 transition-transform duration-200 group-hover:scale-105"
          />
          <span className="font-outfit font-bold text-text-primary text-lg tracking-tight hidden sm:block group-hover:text-accent transition-colors">
            Gabriele Denaro
          </span>
        </Link>

        {/* Center Pill Nav - Desktop */}
        <div className="hidden md:flex items-center bg-white/[0.04] backdrop-blur-2xl border border-line rounded-full px-2 py-1.5  ">
          {navItems.map(({ path, label, icon: Icon, hasDropdown }) => {
            const active = location.pathname === path;

            if (hasDropdown) {
              return (
                <div
                  key={path}
                  className="relative"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  <Link
                    to={path}
                    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`nav-underline flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                      active
                        ? 'nav-underline-active text-accent'
                        : 'text-text-secondary hover:text-text-primary hover:bg-white/[0.06]'
                    }`}
                  >
                    <Icon size={16} weight={active ? 'bold' : 'regular'} />
                    {label}
                    <CaretDown size={10} className={`transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </Link>

                  {/* Dropdown */}
                  {dropdownOpen && (
                    <div
                      data-testid="chi-sono-dropdown"
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-surface/95 backdrop-blur-xl border border-line rounded-2xl   overflow-hidden py-1.5 animate-fade-in-up"
                      style={{ animationDuration: '0.15s' }}
                      onMouseEnter={handleDropdownEnter}
                      onMouseLeave={handleDropdownLeave}
                    >
                      {chiSonoSections.map(({ id, label: secLabel, labelEn, icon: SecIcon }) => (
                        <button
                          key={id}
                          data-testid={`dropdown-${id}`}
                          onClick={() => scrollToSection(id)}
                          className="nav-underline nav-underline-sub w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-accent hover:bg-white/[0.04] transition-all text-left"
                        >
                          <SecIcon size={15} weight="duotone" />
                          {lang === 'en' ? labelEn : secLabel}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                className={`nav-underline flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  active
                    ? 'nav-underline-active text-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-white/[0.06]'
                }`}
              >
                <Icon size={16} weight={active ? 'bold' : 'regular'} />
                {label}
              </Link>
            );
          })}
        </div>

        {/* Right side - Book CTA (desktop) + Mobile Toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scrollToSection('about-projects-section')}
            data-testid="nav-projects-cta"
            data-magnetic
            className="btn-sheen hidden md:flex items-center gap-2 px-4 py-2 bg-white text-bg text-sm font-outfit font-bold rounded-full hover:bg-white/85 transition-all"
          >
            <Book size={15} weight="bold" />
            {t('nav.projects_cta')}
          </button>

          {isAdmin && (
            <div
              data-testid="admin-badge"
              title={lang === 'en' ? 'Admin session active' : 'Sessione admin attiva'}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-green/10 border border-accent-green/30 text-accent-green"
            >
              <ShieldCheck size={14} weight="fill" />
              <span className="font-mono font-bold text-xs uppercase tracking-wide">Admin</span>
            </div>
          )}

          {/* Language switcher */}
          <div className="relative" data-testid="lang-switcher">
            <button
              type="button"
              onClick={() => setLangOpen(!langOpen)}
              data-testid="lang-toggle"
              aria-label={t('lang.switch')}
              className="p-2 text-text-secondary hover:text-accent rounded-full bg-white/[0.04] border border-line transition-colors flex items-center gap-1"
            >
              <Globe size={18} weight="duotone" />
              <span className="font-mono font-bold text-xs uppercase">{lang}</span>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-2 bg-surface/95 backdrop-blur-xl border border-line rounded-xl   py-1 min-w-[140px] animate-fade-in-up z-50">
                <button onClick={() => { setLang('it'); setLangOpen(false); }} data-testid="lang-it"
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-white/[0.04] ${lang === 'it' ? 'text-accent' : 'text-text-primary'}`}>
                  <span className="flex items-center gap-2"><FlagIcon code="it" /> {t('lang.it')}</span>
                  {lang === 'it' && <span className="text-accent">●</span>}
                </button>
                <button onClick={() => { setLang('en'); setLangOpen(false); }} data-testid="lang-en"
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-white/[0.04] ${lang === 'en' ? 'text-accent' : 'text-text-primary'}`}>
                  <span className="flex items-center gap-2"><FlagIcon code="en" /> {t('lang.en')}</span>
                  {lang === 'en' && <span className="text-accent">●</span>}
                </button>
              </div>
            )}
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-text-secondary hover:text-text-primary rounded-full bg-white/[0.04] border border-line"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden mt-3 mx-auto max-w-md bg-surface/95 backdrop-blur-xl border border-line rounded-2xl px-4 py-3   animate-fade-in-up">
          {isAdmin && (
            <div
              data-testid="mobile-admin-badge"
              className="flex items-center justify-center gap-1.5 mb-2 px-3 py-1.5 rounded-xl bg-accent-green/10 border border-accent-green/30 text-accent-green"
            >
              <ShieldCheck size={14} weight="fill" />
              <span className="font-mono font-bold text-xs uppercase tracking-wide">
                {lang === 'en' ? 'Admin session active' : 'Sessione admin attiva'}
              </span>
            </div>
          )}
          {navItems.map(({ path, label, icon: Icon, hasDropdown }) => (
            <div key={path}>
              <Link
                to={path}
                onClick={() => !hasDropdown && setMobileOpen(false)}
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl transition-all ${
                  location.pathname === path
                    ? 'bg-accent/15 text-accent'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon size={18} weight={location.pathname === path ? 'bold' : 'regular'} />
                {label}
              </Link>
              {hasDropdown && (
                <div className="pl-8 space-y-0.5 mb-2">
                  {chiSonoSections.map(({ id, label: secLabel, labelEn, icon: SecIcon }) => (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-muted hover:text-accent transition-all text-left rounded-lg"
                    >
                      <SecIcon size={13} weight="duotone" />
                      {lang === 'en' ? labelEn : secLabel}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => scrollToSection('about-projects-section')}
            className="flex items-center justify-center gap-2 mt-2 px-4 py-2.5 w-full bg-white text-bg text-sm font-outfit font-bold rounded-full hover:bg-white/85 transition-all"
          >
            <Book size={15} weight="bold" />
            {t('nav.projects_cta')}
          </button>

          {/* Mobile language switcher */}
          <div className="mt-2 flex items-center gap-2" data-testid="mobile-lang-switcher">
            <button onClick={() => setLang('it')} data-testid="mobile-lang-it"
              className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-colors flex items-center justify-center gap-1.5 ${lang === 'it' ? 'bg-accent/15 text-accent border-accent/30' : 'text-text-secondary border-line'}`}>
              <FlagIcon code="it" /> IT
            </button>
            <button onClick={() => setLang('en')} data-testid="mobile-lang-en"
              className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-colors flex items-center justify-center gap-1.5 ${lang === 'en' ? 'bg-accent/15 text-accent border-accent/30' : 'text-text-secondary border-line'}`}>
              <FlagIcon code="en" /> EN
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
